import { useQuery } from "@tanstack/react-query";
import { isUiMockMode } from "@/config/features";
import { mapPayLinkApiRow } from "@/domain/payLinks/mapPayLinkApiRow";
import { filterMockPayLinks } from "@/fixtures/payLinks";
import { searchPayLinks } from "@/features/invoices/api/searchPayLinks";
import type { PayLinkRow } from "@/types/api";
import { isInvoiceAmountFilterValid } from "@/domain/invoices/invoiceFilterState";
import { keepPreviousPageData, paginatedSearchQueryOptions } from "@/lib/query/paginatedSearch";
import { isoDateToUnixEnd, isoDateToUnixStart } from "@/utils/format/isoDate";

function majorToMinorUnits(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n * 100);
}

export function usePayLinkSearch(params: {
  customerIds: string[];
  statuses: string[];
  dateFrom: string;
  dateTo: string;
  dueDateFrom: string;
  dueDateTo: string;
  amountMin: string;
  amountMax: string;
  pageSize: number;
  after?: string;
}) {
  const customer_ids =
    params.customerIds.length > 0 ? params.customerIds : undefined;
  const statuses =
    params.statuses.length > 0 ? params.statuses : undefined;
  const amountOk = isInvoiceAmountFilterValid(
    params.amountMin,
    params.amountMax,
  );

  const queryKey = {
    ...params,
    createdFrom: isoDateToUnixStart(params.dateFrom),
    createdTo: isoDateToUnixEnd(params.dateTo),
    dueFrom: isoDateToUnixStart(params.dueDateFrom),
    dueTo: isoDateToUnixEnd(params.dueDateTo),
    amountDueMin: amountOk ? majorToMinorUnits(params.amountMin) : undefined,
    amountDueMax: amountOk ? majorToMinorUnits(params.amountMax) : undefined,
  };

  return useQuery({
    queryKey: ["payLinks", "search", queryKey],
    queryFn: async (): Promise<PayLinkSearchResult> => {
      if (isUiMockMode()) {
        const items = filterMockPayLinks(params);
        return { items, has_more: false, next_cursor: null as string | null };
      }
      const res = await searchPayLinks({
        page: 0,
        page_size: params.pageSize,
        statuses,
        customer_ids,
        after_invoice_id: params.after,
        created_from: queryKey.createdFrom,
        created_to: queryKey.createdTo,
        due_from: queryKey.dueFrom,
        due_to: queryKey.dueTo,
        amount_due_min: queryKey.amountDueMin,
        amount_due_max: queryKey.amountDueMax,
      });
      return {
        items: res.items.map(mapPayLinkApiRow),
        has_more: res.has_more,
        next_cursor: res.next_cursor,
      };
    },
    ...paginatedSearchQueryOptions,
    placeholderData: keepPreviousPageData<PayLinkSearchResult>(queryKey),
  });
}

type PayLinkSearchResult = {
  items: PayLinkRow[];
  has_more: boolean;
  next_cursor: string | null;
};
